/**
 * sendMultiAgentMessage — the panel-side "send a message to the multi-agent run"
 * helper (MA-4 + MA-5). Replaces the dev-agent `session.handleSend` that the IDE
 * panels were wired to.
 *
 * Mirrors the canonical multi-agent send used by MultiAgentRoute.onSend and
 * ResultColumn.FollowUpChips:
 *   1. snapshot the current turn into the thread (so the prior answer stays above)
 *   2. reset() (preserves thread + sessionId)
 *   3. createRun(fullPrompt, sessionId)
 *   4. set sessionId, startRun(run_id, displayText)
 *
 * The `displayText` param (e.g. Tests "Improve") is what shows in the chat as the
 * user bubble, while the full `message` is sent as the backend `user_intent`.
 * When `displayText` is omitted the message itself is shown.
 */
import { useMultiAgentStore } from "@/stores/multiAgentStore";

let panelSendInFlight = false;

export async function sendMultiAgentMessage(
  message: string,
  displayText?: string,
): Promise<void> {
  const intent = (message || "").trim();
  if (!intent || panelSendInFlight) return;

  const store = useMultiAgentStore.getState();
  // Don't fire while a run is in flight (matches MultiAgentRoute.onSend guard).
  const inFlight =
    !store.readOnly &&
    (store.status === "running" ||
      store.status === "starting" ||
      store.status === "planning" ||
      store.status === "awaiting_approval");
  if (inFlight) return;
  panelSendInFlight = true;

  try {
    const sessionId = store.sessionId;
    const parentRunId = store.runId;
    // Snapshot the prior turn (pushTurnToThread self-guards and now preserves
    // artifact-only turns) so the previous turn's code stays visible above.
    if (sessionId) {
      store.pushTurnToThread();
    }
    store.reset();

    const { createRun } = await import("@/services/multi-agent");
    const { readPersistedMultiAgentSession } = await import("@/stores/multiAgentStore");
    // Sticky session: reuse in-memory, else persisted (survives remount) so
    // consecutive messages stay in ONE session until "New session" is clicked.
    const activeSessionId =
      sessionId || parentRunId || readPersistedMultiAgentSession().sessionId || null;
    const created = await createRun(intent, activeSessionId);
    const sid = activeSessionId || created.session_id || created.run_id;
    useMultiAgentStore.setState({ sessionId: sid });
    // Show the display bubble (or the raw intent) in the chat; the backend gets
    // the full prompt as user_intent (already passed to createRun above).
    store.startRun(created.run_id, displayText?.trim() || intent);
  } finally {
    panelSendInFlight = false;
  }
}
